/**
 * Security Configuration Constants
 *
 * XSS Protection and Token Security Best Practices
 */

/**
 * Token expiry buffer in seconds
 * Treats tokens as expired N seconds before actual expiry
 * This prevents race conditions during token refresh
 */
export const TOKEN_EXPIRY_BUFFER_SECONDS = 60;

/**
 * Maximum login attempts before temporary lockout (frontend rate limiting)
 * Note: Backend should also implement rate limiting
 */
export const MAX_LOGIN_ATTEMPTS = 5;

/**
 * Login lockout duration in milliseconds (5 minutes)
 */
export const LOGIN_LOCKOUT_DURATION_MS = 5 * 60 * 1000;

/**
 * Content Security Policy directives for XSS protection
 * Apply these in index.html <meta> tag or server headers
 *
 * Example for index.html:
 * <meta http-equiv="Content-Security-Policy" content="...">
 */
export const CSP_DIRECTIVES = {
  // Only allow scripts from same origin and inline scripts (needed for Angular)
  'script-src': "'self' 'unsafe-inline'",

  // Only allow styles from same origin and inline styles
  'style-src': "'self' 'unsafe-inline'",

  // Only allow images from same origin and data URIs
  'img-src': "'self' data: https:",

  // Only allow fonts from same origin
  'font-src': "'self' data:",

  // Prevent framing (clickjacking protection)
  'frame-ancestors': "'none'",

  // Upgrade insecure requests to HTTPS in production
  'upgrade-insecure-requests': true,
};

/**
 * Build CSP directives with optional additional `connect-src` values.
 *
 * This keeps localhost in the defaults for local development but does not
 * force consumers to read from `environment.ts`. Consumers can call this
 * function with additional hosts (or omit them) or generate their own
 * directives at runtime.
 */
/**
 * Build CSP directives from explicit runtime values.
 *
 * This function does NOT contain any hard-coded host strings. Callers must
 * pass the app `apiUrl` (from `environment.apiUrl`) and can optionally pass
 * `portalUrl` and any extra connect-src entries. This ensures the library
 * never exposes plain host strings and the application controls the values.
 *
 * Example (in application code):
 * import { environment } from '../environments/environment';
 * import { buildCspDirectives } from '@anihis/shared/constants/security.constants';
 * const csp = buildCspDirectives(environment.apiUrl, environment.portalUrl);
 */
export interface BuildCspOptions {
  // allow `unsafe-eval` in script-src (useful for local dev where JIT may run)
  allowUnsafeEval?: boolean;

  // allow `unsafe-inline` in script-src (needed in local dev for Angular esbuild
  // dev-server which injects inline bootstrap/HMR scripts into the HTML)
  allowUnsafeInline?: boolean;

  // extra origins to allow for style-src (e.g. Google Fonts)
  extraStyleSrc?: string[];

  // extra origins to allow for script-src (e.g. Google platform scripts)
  extraScriptSrc?: string[];

  // extra origins to allow for font-src (e.g. fonts.gstatic.com)
  extraFontSrc?: string[];

  // whether to include frame-ancestors directive (NOTE: ignored in meta tags)
  includeFrameAncestors?: boolean;

  // extra origins to allow for img-src (e.g. local Azurite blob storage)
  extraImgSrc?: string[];

  // extra origins to allow for media-src (e.g. video tutorials from blob storage)
  extraMediaSrc?: string[];

  // extra origins to allow for frame-src (e.g. Google Sign-In iframe)
  extraFrameSrc?: string[];
}

export function buildCspDirectives(
  apiUrl: string,
  portalUrl?: string,
  additionalConnectSrc: string[] = [],
  options: BuildCspOptions = {},
): Record<string, string | boolean> {
  if (!apiUrl) {
    throw new Error('apiUrl is required to build CSP directives');
  }

  const connectParts: string[] = ["'self'"];

  // Accept canonical URLs from the caller and include them directly
  const addIfPresent = (url?: string) => {
    if (!url) return;
    // Parse protocol and host without using try/catch. Fall back to raw value.
    const m = String(url).match(
      /^([a-zA-Z][a-zA-Z0-9+.-]*:)?\/\/?([^\/\?#]+)(?:[\/\?#]|$)/,
    );
    if (m && m[2]) {
      const protocol = m[1] ? m[1] : 'https:';
      const host = m[2];
      connectParts.push(`${protocol}//${host}`);
      // Also allow the WebSocket equivalent so SignalR (wss://) connections
      // are not blocked by CSP. https: → wss:, http: → ws:
      if (protocol === 'https:') connectParts.push(`wss://${host}`);
      else if (protocol === 'http:') connectParts.push(`ws://${host}`);
    } else {
      connectParts.push(url as string);
    }
  };

  addIfPresent(apiUrl);
  addIfPresent(portalUrl);

  if (additionalConnectSrc?.length) {
    additionalConnectSrc.forEach((s) => addIfPresent(s));
  }

  // Allow blob: URLs for connect-src to permit fetching blob: resources
  // (e.g. blob:http://localhost:PORT/...) which can be produced by Capacitor
  // or browser APIs. This is intentionally permissive for local/dev flows.
  connectParts.push('blob:');

  // script-src: always allow 'self' and inline for angular templates, optionally add unsafe-eval in dev

  // script-src: always allow 'self', optionally add unsafe-eval in dev and any extra script origins.
  const scriptParts = ["'self'"];
  if (options.allowUnsafeEval) scriptParts.push("'unsafe-eval'");
  if (options.allowUnsafeInline) scriptParts.push("'unsafe-inline'");

  // Allow blob: URLs for script creation (web workers from blob URLs)
  // This is required for some dev-only libs that create workers from blobs.
  // If you want to disallow in production, pass a different options set when
  // calling `buildCspDirectives` in production environments.
  scriptParts.push('blob:');

  // style-src: base + optional extra origins
  const styleParts = ["'self'", "'unsafe-inline'"];
  if (options.extraStyleSrc?.length) styleParts.push(...options.extraStyleSrc);

  if (options.extraScriptSrc?.length)
    scriptParts.push(...options.extraScriptSrc);

  // font-src: base + optional extra origins
  const fontParts = ["'self'", 'data:'];
  if (options.extraFontSrc?.length) fontParts.push(...options.extraFontSrc);

  const directives: Record<string, string | boolean> = {
    'script-src': scriptParts.join(' '),
    'script-src-elem': scriptParts.join(' '),
    // Allow creating workers from blob: URLs; browsers fall back to script-src
    // when worker-src is not set, so set worker-src explicitly to permit blobs.
    'worker-src': "'self' blob:",
    'style-src': styleParts.join(' '),
    'connect-src': connectParts.join(' '),
    'img-src': [
      "'self'",
      'data:',
      'blob:',
      'https:',
      ...(options.extraImgSrc ?? []),
    ].join(' '),
    'media-src': [
      "'self'",
      'blob:',
      'https:',
      ...(options.extraMediaSrc ?? []),
    ].join(' '),
    'font-src': fontParts.join(' '),
    'upgrade-insecure-requests': true,
  };

  // frame-src/object-src: allow blob: for in-app PDF previews (e.g. iframe/object with
  // URL.createObjectURL). object-src covers <object> and <embed> elements.
  const frameParts = ["'self'", 'blob:'];
  if (options.extraFrameSrc?.length) frameParts.push(...options.extraFrameSrc);
  directives['frame-src'] = frameParts.join(' ');
  directives['object-src'] = frameParts.join(' ');

  // frame-ancestors is effective only as a header; include only if explicitly requested
  if (options.includeFrameAncestors) {
    directives['frame-ancestors'] = "'none'";
  }

  return directives;
}

/**
 * Security Headers recommendations for backend
 * These should be set by your API server
 */
export const RECOMMENDED_SECURITY_HEADERS = {
  // Prevent MIME type sniffing
  'X-Content-Type-Options': 'nosniff',

  // Enable browser XSS protection
  'X-XSS-Protection': '1; mode=block',

  // Prevent clickjacking
  'X-Frame-Options': 'DENY',

  // HSTS - Force HTTPS (production only)
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',

  // Referrer policy
  'Referrer-Policy': 'strict-origin-when-cross-origin',

  // Permissions policy
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
};

/**
 * Safe localStorage operations with XSS protection
 * Always sanitize data before storing in localStorage
 */
export class SecureStorage {
  /**
   * Safely get item from localStorage
   * Returns null if item doesn't exist or is invalid
   */
  static getItem(key: string): string | null {
    if (
      typeof localStorage === 'undefined' ||
      typeof localStorage.getItem !== 'function'
    ) {
      return null;
    }

    const item = localStorage.getItem(key);
    if (!item) return null;

    // Basic validation: ensure it's not executable code
    if (item.includes('<script') || item.includes('javascript:')) {
      if (typeof localStorage.removeItem === 'function')
        localStorage.removeItem(key);
      console.warn(
        `Potential XSS attempt detected in localStorage key: ${key}`,
      );
      return null;
    }

    return item;
  }

  /**
   * Safely set item in localStorage
   * Validates input to prevent XSS
   */
  static setItem(key: string, value: string): boolean {
    if (
      typeof localStorage === 'undefined' ||
      typeof localStorage.setItem !== 'function'
    ) {
      return false;
    }

    // Validate input
    if (typeof value !== 'string') {
      console.error('Only strings can be stored in localStorage');
      return false;
    }

    // Check for potential XSS
    if (value.includes('<script') || value.includes('javascript:')) {
      console.warn('Potential XSS attempt prevented');
      return false;
    }

    localStorage.setItem(key, value);
    return true;
  }

  /**
   * Clear all items from localStorage except whitelisted keys
   */
  static clearExcept(whitelist: string[] = ['lang', 'language']): void {
    if (
      typeof localStorage === 'undefined' ||
      typeof localStorage.clear !== 'function'
    )
      return;

    const keysToKeep: { [key: string]: string | null } = {};
    // Save whitelisted items
    whitelist.forEach((key) => {
      keysToKeep[key] =
        typeof localStorage.getItem === 'function'
          ? localStorage.getItem(key)
          : null;
    });

    // Clear all
    if (typeof localStorage.clear !== 'function') return;
    localStorage.clear();

    // Restore whitelisted items
    Object.entries(keysToKeep).forEach(([key, value]) => {
      if (value && typeof localStorage.setItem === 'function') {
        localStorage.setItem(key, value);
      }
    });
  }
}
